import React, {useEffect, useMemo, useState} from "react";
import {InputNumber, Segmented, Spin, Table} from "antd";
import {SwapOutlined, SwapRightOutlined} from "@ant-design/icons";
import {ReactComponent as AmericanFlagIcon} from "../../icons/flags/american.svg";
import {ReactComponent as RussianFlagIcon} from "../../icons/flags/russia.svg";
import {getRates} from "../../api/rates";
import Loading from "../../components/modal/Loading";

const FLAG_ICONS = {
  USD: <AmericanFlagIcon/>,
  RUB: <RussianFlagIcon/>,
};

const CURRENCY_LABELS = {
  USD: "$",
  RUB: "₽",
};

const getMethodTitle = (method) => method?.title ?? "Любой метод";

const AllRatesContent = () => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    document.title = "USDSeller | Все курсы";

    const fetchRates = () => {
      getRates()
        .then((data) => {
          setRates(data);
          setUpdatedAt(new Date());
          setError(null);
        })
        .catch((err) => setError(err))
        .finally(() => setLoading(false));
    };

    fetchRates();
    const interval = setInterval(fetchRates, 60_000);
    return () => clearInterval(interval);
  }, []);

  const [direction, setDirection] = useState("USD → RUB");
  const [selectedPairId, setSelectedPairId] = useState(null);
  const [amount, setAmount] = useState(1);

  const isUsdToRub = direction === "USD → RUB";

  const filtered = useMemo(() => {
    const base = rates.filter((r) =>
      isUsdToRub
        ? r.currency_from === "USD" && r.currency_to === "RUB"
        : r.currency_from === "RUB" && r.currency_to === "USD"
    );
    return isUsdToRub ? base.filter((r) => r.method_from) : base;
  }, [isUsdToRub, rates]);

  const selectedPair = useMemo(
    () => filtered.find((r) => r.id === selectedPairId) ?? filtered[0] ?? null,
    [filtered, selectedPairId]
  );

  const calcUsdToRubRate = (pair) => {
    if (!pair) return 0;
    const buy = parseFloat(pair.buy_rate);
    const sell = parseFloat(pair.sell_rate);
    const k = parseFloat(pair.ratio_rate ?? "1");

    if (pair.currency_from === "USD") {
      return buy / (Number.isFinite(k) && k > 0 ? k : 1);
    }
    return sell * (Number.isFinite(k) && k > 0 ? k : 1);
  };

  const usdToRubRate = selectedPair ? calcUsdToRubRate(selectedPair) : 0;

  const converted = (() => {
    if (!selectedPair || !amount) return "0.00";
    if (selectedPair.currency_from === "USD") {
      return (amount * usdToRubRate).toFixed(2);
    }
    return (amount / usdToRubRate).toFixed(2);
  })();

  const fromSymbol = selectedPair ? CURRENCY_LABELS[selectedPair.currency_from] : "";
  const toSymbol = selectedPair ? CURRENCY_LABELS[selectedPair.currency_to] : "";

  const columns = [
    {
      title: "Отправляете",
      dataIndex: "method_from",
      key: "from",
      minWidth: 160,
      render: (_, record) => (
        <div className="all-rates-cell-method">
          <span className="all-rates-flag">{FLAG_ICONS[record.currency_from]}</span>
          <span className="font-medium">{record.currency_from}</span>
          <span className="text-[#5C607C] all-rates-cell-method-name">
            ({getMethodTitle(record.method_from)})
          </span>
        </div>
      ),
    },
    {
      title: "",
      key: "arrow",
      width: 40,
      onHeaderCell: () => ({className: "!hidden lg:!table-cell"}),
      onCell: () => ({className: "!hidden lg:!table-cell"}),
      render: () => <SwapRightOutlined className="text-[#B0B2C0]"/>,
    },
    {
      title: "Получаете",
      dataIndex: "method_to",
      key: "to",
      minWidth: 160,
      render: (_, record) => (
        <div className="all-rates-cell-method">
          <span className="all-rates-flag">{FLAG_ICONS[record.currency_to]}</span>
          <span className="font-medium">{record.currency_to}</span>
          <span className="text-[#5C607C] all-rates-cell-method-name">
            ({getMethodTitle(record.method_to)})
          </span>
        </div>
      ),
    },
    {
      title: "Курс",
      key: "rate",
      minWidth: 90,
      render: (_, record) => {
        const rate = calcUsdToRubRate(record);
        return (
          <span className="font-medium text-[#040E44]">
            {rate >= 1 ? rate.toFixed(4) : rate.toFixed(6)}
          </span>
        );
      },
    },
  ];

  return (
    <>
      {loading && <Loading/>}
      {error && !loading && (
        <div className="home-main max-w-[93%] lg:max-w-[1500px] lg:w-[1200px]">
          <div className="home-main-inside flex items-center justify-center lg:min-h-[900px!important]">
            <span className="text-red-500 body-1">Не удалось загрузить курсы. Попробуйте позже.</span>
          </div>
        </div>
      )}
    {!error && <div className="home-main max-w-[93%] lg:max-w-[1500px] lg:w-[1200px]">
      <div className="home-main-inside lg:min-h-[900px!important]">
        <div className="home-main-inside-header mt-3">
          <span className="home-main-inside-text-h1 heading-2">
            Все курсы обмена валют
          </span>
          <p className="home-main-inside-text-p body-1">
            Выберите направление и метод для расчёта
          </p>
        </div>

        {/* Переключатель направления */}
        <div className="flex justify-center">
          <Segmented
            value={direction}
            onChange={(val) => {
              setDirection(val);
              setSelectedPairId(null);
              setAmount(1);
            }}
            options={["USD → RUB", "RUB → USD"]}
            className="all-rates-segmented"
          />
        </div>

        {/* Таблица */}
        <div className="all-rates-table-wrap">
          <Table
            dataSource={filtered}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
            className="all-rates-table"
            rowClassName={(record) =>
              record.id === selectedPair?.id
                ? "all-rates-row-selected"
                : "all-rates-row"
            }
            onRow={(record) => ({
              onClick: () => setSelectedPairId(record.id),
              style: {cursor: "pointer"},
            })}
          />
        </div>


        {/* Калькулятор */}
        {selectedPair && (
          <div className="all-rates-calculator">
            <div className="all-rates-calc-header subtitle-3">
              <span className="text-[#5C607C]">
                {getMethodTitle(selectedPair.method_from)} →{" "}
                {getMethodTitle(selectedPair.method_to)}
              </span>
            </div>

            <div className="all-rates-calc-body">
              <div className="all-rates-calc-input">
                <label className="content-label body-2">Отправляете</label>
                <div className="flex items-center gap-2">
                  <span className="text-[#5C607C]">{fromSymbol}</span>
                  <InputNumber
                    min={0}
                    value={amount}
                    onChange={(val) => setAmount(val ?? 0)}
                    controls={false}
                    className="all-rates-input-number"
                  />
                  <span className="text-[#5C607C] font-medium">
                    {selectedPair.currency_from}
                  </span>
                </div>
              </div>

              <div className="all-rates-calc-result">
                <label className="content-label body-2">Получаете</label>
                <div className="flex items-center gap-2">
                  <span className="text-[#040E44] font-bold text-xl">
                    {toSymbol}
                    {converted}
                  </span>
                  <span className="text-[#5C607C] font-medium">
                    {selectedPair.currency_to}
                  </span>
                </div>
              </div>
            </div>

            {/* Единый курс: 1 USD = X RUB */}
            <div className="all-rates-calc-rate body-2 text-[#5C607C]">
              1 USD = {usdToRubRate.toFixed(4)} RUB
            </div>
          </div>
        )}

        {/* Дата обновления */}
        {updatedAt && (
          <p className="text-xs text-[#B0B2C0] text-right mt-2">
            Курс актуален на{" "}
            {updatedAt.toLocaleDateString("ru-RU", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              timeZone: "UTC",
            })}{" "}
            {updatedAt.toLocaleTimeString("ru-RU", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
              timeZone: "UTC",
            })}{" "}
            UTC
          </p>
        )}
      </div>
    </div>}
    </>
  );
};

export default AllRatesContent;